import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Zap, Shield, Users, TrendingUp, Code2, Package, HandCoins, Sparkles } from "lucide-react";

/**
 * Careers page — asymmetric left-aligned marketing layout matching the Home.jsx
 * aesthetic (Fraunces display, Outfit body, --ink / --blue / --wash palette).
 */
export default function Careers() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="m-shell" data-testid="careers-page">
      <Nav />
      <Hero />
      <WhyJoin />
      <Values />
      <Roles />
      <Perks />
      <HowToApply />
      <Footer />
    </div>
  );
}

/* ---------- Nav (mirrors Home.jsx so the brand feels continuous) ---------- */
function Nav() {
  return (
    <header className="m-nav" data-testid="careers-navbar">
      <Link to="/" className="m-brand" data-testid="careers-brand-home">
        <img src="/logo.png" alt="GoPrintPack" className="m-brand-logo" />
      </Link>
      <nav className="m-nav-links">
        <Link to="/" data-testid="careers-nav-home">Home</Link>
        <Link to="/contact" data-testid="careers-nav-contact">Contact</Link>
        <Link to="/partner" data-testid="careers-nav-partner">Partner with us</Link>
        <Link to="/careers" data-testid="careers-nav-careers">Careers</Link>
      </nav>
      <div className="m-nav-actions">
        <Link to="/login" className="m-ghost" data-testid="careers-nav-signin">Sign in</Link>
        <Link to="/register" className="m-cta" data-testid="careers-nav-launch">
          Launch workspace <ArrowRight size={14}/>
        </Link>
      </div>
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="car-hero" data-testid="careers-hero">
      <div className="car-hero-copy">
        <p className="eyebrow" data-testid="careers-hero-eyebrow">
          CAREERS · BUILD INDIA'S FIRST SEALED-BID PRINT PLATFORM
        </p>
        <h1 className="car-h1" data-testid="careers-hero-title">
          We're hiring the people <br/>who'll redraw <em>print &amp; packaging</em> procurement.
        </h1>
        <p className="car-hero-sub" data-testid="careers-hero-sub">
          GoPrintPack™ is an asset-light marketplace that routes brand orders to verified
          Indian presses via anonymous reverse-auctions, escrow protection and blind-shipped
          B2B delivery. We're a small, scrappy team sitting at the intersection of
          <b> software, logistics, GST compliance and manufacturing.</b> If you want to ship
          work that an entire industry notices — read on.
        </p>
        <div className="car-hero-cta">
          <a
            href="mailto:info@goprintpack.com?subject=Application%20%E2%80%94%20GoPrintPack%E2%84%A2"
            className="m-cta m-cta-lg"
            data-testid="careers-hero-apply"
          >
            <Mail size={16}/> Apply at info@goprintpack.com
          </a>
          <a href="#roles" className="m-outline" data-testid="careers-hero-see-roles">
            See open roles ↓
          </a>
        </div>
        <div className="car-badges">
          <Badge testid="badge-remote"  dot="#10B981">Remote-friendly</Badge>
          <Badge testid="badge-equity"  dot="#2563EB">Early team · high ownership</Badge>
          <Badge testid="badge-india"   dot="#F59E0B">Made in Maharashtra 🇮🇳</Badge>
        </div>
      </div>
      <div className="car-hero-visual" data-testid="careers-hero-visual">
        <div className="car-card">
          <div className="car-card-head">
            <span className="car-card-dot" />
            <span className="car-card-title">live_auctions.jsonl</span>
            <span className="car-card-tag">tail -f</span>
          </div>
          <pre className="car-card-body">
{`{ "job":"packaging-run-#8142", "bids":4,  "status":"live" }
{ "job":"visiting-cards-#8143", "bids":7,  "status":"awarded" }
{ "job":"corrugated-#8144",     "bids":2,  "status":"live" }
{ "job":"labels-#8145",         "bids":5,  "status":"dispatched" }
{ "job":"flex-pouch-#8146",     "bids":9,  "status":"live" }
...
₹4.1 Cr escrow handled this quarter.
340+ brands. 125+ verified presses. 1 small team.`}
          </pre>
        </div>
        <div className="car-float">
          <Sparkles size={16}/> <span>You could build the next 10 quarters.</span>
        </div>
      </div>
    </section>
  );
}

function Badge({ children, dot, testid }) {
  return (
    <div className="car-badge" data-testid={testid}>
      <span className="car-badge-dot" style={{ background: dot }} />
      <span>{children}</span>
    </div>
  );
}

/* ---------- Why Join ---------- */
function WhyJoin() {
  const stats = [
    { k: "₹4.1 Cr+", v: "Escrow handled this quarter" },
    { k: "340+",     v: "Brands actively procuring" },
    { k: "125+",     v: "Verified Indian presses" },
    { k: "0 → 1",    v: "Stage you'd be joining at" },
  ];
  return (
    <section className="car-why" data-testid="careers-why">
      <div className="car-why-head">
        <p className="eyebrow">WHY GOPRINTPACK</p>
        <h2 className="car-h2">
          Small team. Real revenue. A problem old enough to be worth solving.
        </h2>
        <p className="car-why-lead">
          Print &amp; packaging is a ₹1.1-lakh-crore category in India — fragmented,
          relationship-driven, famously opaque on pricing. We're building the software
          substrate that makes it transparent: sealed auctions, admin-set price floors,
          escrow-held payouts, GST-compliant invoicing, blind-shipped delivery. Every line
          of code we ship moves real money and real cardboard.
        </p>
      </div>
      <div className="car-stats">
        {stats.map((s, i) => (
          <div className="car-stat" key={i} data-testid={`careers-stat-${i}`}>
            <div className="car-stat-k">{s.k}</div>
            <div className="car-stat-v">{s.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Values ---------- */
function Values() {
  const items = [
    {
      icon: <Shield size={20}/>,
      title: "Buyer protection is non-negotiable",
      body: "Escrow holds every rupee until the buyer confirms delivery. We don't ship features that quietly erode this guarantee — even when it's inconvenient.",
    },
    {
      icon: <Zap size={20}/>,
      title: "Default to shipping",
      body: "Two-week scopes beat two-quarter roadmaps. We'd rather ship 80% today, learn, and iterate — than draft specs for the perfect 100%.",
    },
    {
      icon: <Users size={20}/>,
      title: "Three sides, one platform",
      body: "Buyers, printers, agencies — each with different incentives. The job is to design flows where the smartest decision for one is still fair for the other two.",
    },
    {
      icon: <TrendingUp size={20}/>,
      title: "Compound craft",
      body: "Small team means every PR teaches the next. We review carefully, write things down (PRD, policies, API contracts) and expect craft to compound year over year.",
    },
  ];
  return (
    <section className="car-values" data-testid="careers-values">
      <p className="eyebrow">HOW WE WORK</p>
      <h2 className="car-h2">The four things we actually believe.</h2>
      <div className="car-values-grid">
        {items.map((it, i) => (
          <div key={i} className="car-value-card" data-testid={`careers-value-${i}`}>
            <div className="car-value-icon">{it.icon}</div>
            <h3>{it.title}</h3>
            <p>{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Open Roles ---------- */
function Roles() {
  const roles = [
    {
      id: "fs-eng",
      icon: <Code2 size={18}/>,
      title: "Full-stack Engineer (React + FastAPI)",
      type: "Full-time · Remote (India)",
      level: "Mid–Senior · 3–7 yrs",
      bullets: [
        "Own real surfaces end-to-end — Workspace, Auction engine, Dispute flows.",
        "Comfortable in React 19, Tailwind, FastAPI, async MongoDB (Motor), JWT auth.",
        "Have shipped at least one production payment or escrow flow before.",
      ],
    },
    {
      id: "ops",
      icon: <Package size={18}/>,
      title: "Supplier Operations Lead",
      type: "Full-time · Kopargaon / Pune / Remote",
      level: "4+ yrs in print or packaging operations",
      bullets: [
        "Onboard and verify printers — KYC, capabilities, capacity, quality grading.",
        "Partner with Shiprocket on B2B pickup hubs and dispatch SLAs.",
        "Spot bad bids and bad behaviour before buyers do.",
      ],
    },
    {
      id: "gm",
      icon: <HandCoins size={18}/>,
      title: "Growth / Partnerships Manager",
      type: "Full-time · Remote",
      level: "3+ yrs B2B SaaS or marketplace growth",
      bullets: [
        "Own buyer acquisition funnel — content, SEO, lifecycle, outbound to brand ops teams.",
        "Launch design-agency and corrugator partnerships that compound.",
        "Treat ARR, GMV and net-revenue retention as first-class citizens.",
      ],
    },
    {
      id: "intern",
      icon: <Sparkles size={18}/>,
      title: "Design &amp; Product Intern",
      type: "6-month internship · Remote",
      level: "Final-year or new grad with a strong portfolio",
      bullets: [
        "Prototype in Figma on day one, ship Tailwind in week two.",
        "Audit copy, flows and emails for buyers, printers and agencies.",
        "Pair with the founders on roadmap, PRD and release notes.",
      ],
    },
  ];
  return (
    <section className="car-roles" id="roles" data-testid="careers-roles">
      <div className="car-roles-head">
        <p className="eyebrow">OPEN ROLES</p>
        <h2 className="car-h2">Four seats open right now.</h2>
        <p className="car-roles-lead">
          Don't see a perfect fit? <b>Send us a note anyway.</b> We'd rather talk to a
          great builder six months early than miss them.
        </p>
      </div>
      <div className="car-roles-list">
        {roles.map((r) => (
          <article className="car-role" key={r.id} data-testid={`careers-role-${r.id}`}>
            <div className="car-role-head">
              <div className="car-role-icon">{r.icon}</div>
              <div className="car-role-meta">
                <h3 dangerouslySetInnerHTML={{ __html: r.title }} />
                <p className="car-role-sub">
                  <span>{r.type}</span>
                  <span className="car-role-dot">·</span>
                  <span>{r.level}</span>
                </p>
              </div>
              <a
                href={`mailto:info@goprintpack.com?subject=Application%20%E2%80%94%20${encodeURIComponent(r.title.replace(/&amp;/g,'&'))}`}
                className="car-role-apply"
                data-testid={`careers-apply-${r.id}`}
              >
                Apply <ArrowRight size={14}/>
              </a>
            </div>
            <ul className="car-role-bullets">
              {r.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------- Perks ---------- */
function Perks() {
  const perks = [
    { title: "High-ownership early team",    body: "You'll ship things customers see within week one. Review loops are short, decisions are logged, credit is loud." },
    { title: "Remote-first, Maharashtra-rooted", body: "Work from anywhere in India. Twice a quarter we meet in Kopargaon or Pune for a working week." },
    { title: "Learning budget",              body: "₹25,000 per year for books, courses, conferences — pick whatever makes you a better builder." },
    { title: "Macbook of your choice",       body: "You pick the spec. We cover it, insure it, and refresh every 3 years." },
    { title: "Transparent compensation",     body: "Market-benchmarked salary + meaningful ESOP for full-time hires. We publish bands internally." },
    { title: "Real holidays",                body: "20 days paid + public holidays. Minimum 10 days must be taken — no hoarding, no guilt." },
  ];
  return (
    <section className="car-perks" data-testid="careers-perks">
      <p className="eyebrow">PERKS</p>
      <h2 className="car-h2">What you'd get, beyond the title.</h2>
      <div className="car-perks-grid">
        {perks.map((p, i) => (
          <div key={i} className="car-perk" data-testid={`careers-perk-${i}`}>
            <h4>{p.title}</h4>
            <p>{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- How to Apply (big CTA) ---------- */
function HowToApply() {
  return (
    <section className="car-apply" data-testid="careers-apply-cta">
      <div className="car-apply-inner">
        <p className="eyebrow car-apply-eyebrow">HOW TO APPLY</p>
        <h2 className="car-h2">One email. Three attachments. We reply in a week.</h2>
        <ol className="car-apply-steps">
          <li>
            <span className="car-step-k">1</span>
            <div>
              <b>Send to <a href="mailto:info@goprintpack.com">info@goprintpack.com</a></b>
              <br/>Subject line: <code>Application — [Role you want]</code>
            </div>
          </li>
          <li>
            <span className="car-step-k">2</span>
            <div>
              <b>Attach your CV, portfolio and / or GitHub.</b>
              <br/>Add one paragraph on the hardest problem you shipped last year — bugs you solved, people you convinced, trade-offs you made.
            </div>
          </li>
          <li>
            <span className="car-step-k">3</span>
            <div>
              <b>We reply within 7 working days.</b>
              <br/>You'll either get a short take-home (paid for engineers, scoped) or a 30-minute founder chat. No ghosting, promise.
            </div>
          </li>
        </ol>
        <div className="car-apply-ctarow">
          <a
            href="mailto:info@goprintpack.com?subject=Application%20%E2%80%94%20GoPrintPack%E2%84%A2"
            className="m-cta m-cta-lg"
            data-testid="careers-primary-email"
          >
            <Mail size={16}/> info@goprintpack.com
          </a>
          <Link to="/contact" className="m-outline" data-testid="careers-contact-link">
            Rather talk first? Contact us
          </Link>
        </div>
        <p className="car-apply-note">
          Tejas Industries (Proprietorship Firm) · GSTIN 27BWSPS9801C1Z2 · Kopargaon, Maharashtra.
          We're an equal-opportunity workplace — gender, caste, religion, orientation and disability
          do not enter any hiring decision. Just the work.
        </p>
      </div>
    </section>
  );
}

/* ---------- Footer (short) ---------- */
function Footer() {
  return (
    <footer className="car-foot" data-testid="careers-footer">
      <div>
        <img src="/logo.png" alt="GoPrintPack" className="car-foot-logo"/>
        <p>Smart platform for print &amp; packaging procurement.</p>
      </div>
      <div className="car-foot-links">
        <Link to="/">Home</Link>
        <Link to="/contact">Contact</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
      </div>
      <div className="car-foot-small">
        © Tejas Industries. GoPrintPack™ is a trademark of Tejas Industries.
      </div>
    </footer>
  );
}
